import { observer } from 'mobx-react';
import moment from 'moment';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FoodicsBottomSyncTip from '../FoodicsBottomSyncTip/FoodicsBottomSyncTip';
import FoodicsTopSyncTip from '../FoodicsTopSyncTip/FoodicsTopSyncTip';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { SyncStatus } from 'src/constants/integrations';
import { BUTTON_TITLE } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { history } from 'src/services/history';
import { confirmMessage } from 'src/utils/modalConfirmations';
import CardWithCheckboxes from 'src/view/components/CardWithCheckboxes/CardWithCheckboxes';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import CustomTooltip, { TOOLTIP_POSITION, TOOLTIP_TYPE } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Picker from 'src/view/components/Controls/Pickers/Picker/Picker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import SpinLoader from 'src/view/components/Controls/SpinLoader/SpinLoader';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import Text, { TEXT } from 'src/view/components/Text/Text';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import foodicsIntegrationStore from 'src/view/screens/SettingsScreen/store/foodicsIntegrationStore';
import { TBooleanInNumber } from '~/types/main';
import './style.scss';
import { getTranslate } from '~/utils/helpers';
import { IFoodicsSettingsBranch } from '~/types/integrations';
import { appStore } from '~/mobx/appStore';

type FoodicsBranchView = {
  label: string;
  value: string;
};

const FoodicsAuthorizedForm: React.FC = observer(() => {
  const timeout: React.MutableRefObject<NodeJS.Timeout | null> = useRef(null);
  useEffect(() => {
    return () => {
      if (timeout.current !== null) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, []);

  const [expandedGroups, setExpandedGroups] = useState({});
  const isFirstSync: boolean = useMemo(
    () => foodicsIntegrationStore.integrationSettings?.settings.f5_last_sync_date === null,
    [foodicsIntegrationStore.integrationSettings?.settings.f5_last_sync_date]
  );
  const lastSyncDate: string = useMemo(
    () =>
      moment(foodicsIntegrationStore.integrationSettings?.settings.f5_last_sync_date)
        .subtract(new Date().getTimezoneOffset(), 'minutes')
        .format('DD/MM/YYYY, h:mm A'),
    [foodicsIntegrationStore.integrationSettings?.settings.f5_last_sync_date]
  );
  const syncInProgress: boolean = useMemo(
    () =>
      // DO not change to just foodicsIntegrationStore.integrationSettings?.settings.sync_status because sync_status === 0 is progress sync
      Boolean(
        foodicsIntegrationStore.integrationSettings?.settings.sync_status !== undefined &&
          foodicsIntegrationStore.integrationSettings?.settings.sync_status !== null &&
          [SyncStatus.PENDING, SyncStatus.PROCESSING].includes(foodicsIntegrationStore.integrationSettings?.settings.sync_status)
      ),
    [foodicsIntegrationStore.integrationSettings?.settings.sync_status]
  );
  const isAutoSyncEnabled: TBooleanInNumber = useMemo(
    () => (isFirstSync || foodicsIntegrationStore.integrationSettings?.settings.f5_sync_auto ? 1 : 0),
    [foodicsIntegrationStore.integrationSettings?.settings.f5_sync_auto]
  );

  const onLogoutClicked = () => {
    confirmMessage({
      title: localeStore.t('t2_foodics_unauth_question'),
      message: localeStore.t('pos_tip6'),
      onConfirm: () =>
        foodicsIntegrationStore.logout().then(() => {
          integrationsStore.fetchPosRestrictionsSettings();
          appStore.fetchSettings();
          return history.push(SCREENS.SETTINGS_INTEGRATIONS());
        }),
      confirmBtnTxt: localeStore.t('t2_foodics_unauth'),
      cancelBtnTxt: localeStore.t(BUTTON_TITLE.CANCEL),
      redConfirmBtn: true,
    });
  };

  const onSyncClicked = () => {
    foodicsIntegrationStore.runSync();
    if (timeout.current !== null) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    timeout.current = setTimeout(() => {
      foodicsIntegrationStore.getSettings();
    }, 10000 * 60); // 10 minutes
  };

  const onGroupExpanded = (groupName: string, expanded: boolean) => {
    setExpandedGroups({
      ...expandedGroups,
      [groupName]: expanded,
    });
  };

  const isAnyExpanded = () => {
    const values = Object.values(expandedGroups);
    return values.includes(true);
  };

  const getEssencesSettings = () => {
    if (!foodicsIntegrationStore.integrationSettings) {
      return null;
    }
    return Object.entries(foodicsIntegrationStore.integrationSettings.settings.essenceAdvancedIntegrationType).map(([groupName, items]) => {
      const _items = Object.entries(items).map(([key, value]) => {
        return {
          name: key,
          value,
          onChange: (name: string | number, value: boolean | undefined) => {
            foodicsIntegrationStore.updateSetting(name, value);
          },
          disabled: foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing,
        };
      });
      return <CardWithCheckboxes title={localeStore.t(groupName)} items={_items} onExpand={(expanded) => onGroupExpanded(groupName, expanded)} />;
    });
  };

  const onSelectFoodicsBranches = useCallback((branches: FoodicsBranchView[]) => {
    if (!branches.length) {
      foodicsIntegrationStore.updateSetting('branches', []);
      return;
    }

    foodicsIntegrationStore.updateSetting(
      'branches',
      branches.map((branch) => branch.value)
    );
  }, []);

  const extractBranchForView = useRef(
    (foodicsBranch: IFoodicsSettingsBranch): FoodicsBranchView => ({
      label: getTranslate(foodicsBranch.name),
      value: foodicsBranch.id,
    })
  ).current;

  return (
    <ScrollableBlock>
      {syncInProgress && (
        <div className="foodics-authorized-form__loader">
          <SpinLoader />
        </div>
      )}
      <div className="foodics-authorized-form mt-6">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex flex-column">
            <div className="foodics-authorized-form__title">{localeStore.t('t2_foodics_account')}</div>
            <div className="mt-4">
              <span className="mr-2 foodics-authorized-form__account-info">
                {foodicsIntegrationStore.integrationSettings?.settings.business_name}
              </span>
              <span className="foodics-authorized-form__account-info">
                {foodicsIntegrationStore.integrationSettings?.settings.business_reference}
              </span>
            </div>
          </div>
          <div className="foodics-authorized-form__safe-mode-toggle-wrapper d-none" />
        </div>
        <div className="d-flex flex-column mt-6 mb-4">
          <div className="d-flex align-items-center justify-content-start">
            <span className="mr-3 foodics-authorized-form__title">{localeStore.t('t2_foodics_menu')}</span>
            <CustomTooltip position={TOOLTIP_POSITION.FIXED} type={TOOLTIP_TYPE.INFO} message="t2_foodics_mg_tip">
              <Icon name={Icons.Info} size={20} className="cursor-pointer color-gray2" />
            </CustomTooltip>
          </div>
          <div className="d-flex align-items-center justify-content-start mt-4">
            <Picker
              onSelect={(options: IPickerOption[]) => foodicsIntegrationStore.updateSetting('f5_menu_group', options[0].value)}
              selectedOptions={foodicsIntegrationStore.getSelectedGroups}
              options={foodicsIntegrationStore.getGroupsSelectOptions}
              className="foodics-authorized-form__group-select"
              placeholder={localeStore.t('select_menu_group')}
              hideResetIcon
              disabled={foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing}
              emptyText={localeStore.t('sorry_no_menu_groups')}
            />
            {isFirstSync && (
              <CustomTooltip position={TOOLTIP_POSITION.FIXED} type={TOOLTIP_TYPE.INFO} message="t2_foodics_entities_tip">
                <Icon name={Icons.Info} size={20} className="cursor-pointer color-gray2" />
              </CustomTooltip>
            )}
          </div>
          <Fragment>
            <div className="d-flex align-items-center justify-content-start  mt-6">
              <span className="mr-3 foodics-authorized-form__title">{localeStore.t('selected_branches')}</span>

              <CustomTooltip position={TOOLTIP_POSITION.FIXED} type={TOOLTIP_TYPE.INFO} message="t2_foodics_branches_tip">
                <Icon name={Icons.Info} size={20} className="cursor-pointer color-gray2" />
              </CustomTooltip>
            </div>

            <Picker
              placeholder={localeStore.t('all_branches')}
              key="branches-picker"
              options={foodicsIntegrationStore.foodicsBranches.map(extractBranchForView)}
              selectedOptions={foodicsIntegrationStore.foodicsSelectedBranches.map(extractBranchForView)}
              onSelect={onSelectFoodicsBranches}
              hideResetIcon
              isMulti
              searchable
              className="foodics-authorized-form__group-select mr-2 mt-4"
              disabled={foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing}
              emptyText={localeStore.t('no_branches_found')}
            />
          </Fragment>
        </div>

        {!isFirstSync && (
          <div className="d-flex align-items-start justify-content-start mb-6 mr-4 foodics-authorized-form__essences-settings">
            {getEssencesSettings()}
          </div>
        )}
        <div className="d-flex flex-column">
          <div className="foodics-authorized-form__title">{localeStore.t('other_settings')}</div>
          <div className="d-flex flex-row align-items-center mt-5">
            <Switcher
              className="mr-4"
              status={isAutoSyncEnabled}
              onChange={() => foodicsIntegrationStore.updateSetting('f5_sync_auto', !isAutoSyncEnabled)}
              disabled={isFirstSync || foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing}
            />
            <Text
              className="mr-3 foodics-authorized-form__checkbox-title"
              type={TEXT.CUSTOM}
              text="t2_foodics_autosync"
              disabled={isFirstSync || foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing}
            />
            <CustomTooltip position={TOOLTIP_POSITION.FIXED} type={TOOLTIP_TYPE.INFO} message="t2_foodics_autosync_tip">
              <Icon name={Icons.Info} size={20} className="cursor-pointer color-gray2" />
            </CustomTooltip>
          </div>
          <div className="d-flex flex-row align-items-center mt-4">
            <Switcher
              className="mr-4"
              status={foodicsIntegrationStore.integrationSettings?.settings.f5_delivery_autocomplete || 0}
              onChange={() =>
                foodicsIntegrationStore.updateSetting(
                  'f5_delivery_autocomplete',
                  !(foodicsIntegrationStore.integrationSettings?.settings.f5_delivery_autocomplete || 0)
                )
              }
              disabled={foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing}
            />
            <Text
              className="mr-3 foodics-authorized-form__checkbox-title"
              type={TEXT.CUSTOM}
              text="t2_foodics_pos_complete"
              disabled={foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing}
            />
            <CustomTooltip position={TOOLTIP_POSITION.FIXED} type={TOOLTIP_TYPE.INFO} message="t2_foodics_pos_complete_tip">
              <Icon name={Icons.Info} size={20} className="cursor-pointer color-gray2" />
            </CustomTooltip>
          </div>
        </div>
        <div className={`d-flex flex-column foodics-authorized-form__footer-wrapper ${isFirstSync ? 'mt-47' : isAnyExpanded() ? 'mt-6' : 'mt-16'}`}>
          <FoodicsTopSyncTip
            syncInProgress={syncInProgress}
            isFirstSync={isFirstSync}
            syncStatus={foodicsIntegrationStore.integrationSettings?.settings.sync_status}
            lastSyncDate={lastSyncDate}
          />
          <FoodicsBottomSyncTip
            syncInProgress={syncInProgress}
            isFirstSync={isFirstSync}
            syncAuto={Boolean(foodicsIntegrationStore.integrationSettings?.settings.f5_sync_auto)}
          />
          <div className="d-flex align-items-center justify-content-between mb-4 mt-4">
            <div className="d-flex align-items-center justify-content-start">
              <Button className="mr-4" onClick={onSyncClicked} disabled={foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing}>
                {localeStore.t('t2_foodics_sync')}
              </Button>
              <Button
                appearance="destructive-secondary"
                size="medium"
                stretched
                title={localeStore.t('t2_foodics_unauth')}
                onClick={onLogoutClicked}
                disabled={foodicsIntegrationStore.isLoading || foodicsIntegrationStore.isRefreshing}
              />
            </div>
            <div className="d-flex align-items-center justify-content-between mr-4">
              {/* <div className='foodics-authorization-form__link cursor-pointer mr-6'>*/}
              {/*  {localeStore.t('pos_log1')}*/}
              {/* </div>*/}
              {/* <div className='foodics-authorization-form__link cursor-pointer'>*/}
              {/*  {localeStore.t('pos_log2')}*/}
              {/* </div>*/}
            </div>
          </div>
        </div>
      </div>
    </ScrollableBlock>
  );
});

export default FoodicsAuthorizedForm;
